/**
 * Navigation Component - UX1: Categorized Navigation
 *
 * Implements Miller's Law (7±2 items) by organizing navigation into 5 semantic categories
 * instead of 13+ flat items. Reduces cognitive load and improves information architecture.
 *
 * Features:
 * - 5 category dropdown navigation (About, Work, Content, Platform, Admin)
 * - Progressive disclosure (hover to reveal subcategories)
 * - Keyboard navigation (Tab, Arrow keys, Enter, Escape)
 * - Mobile hamburger menu with category sections
 * - Adaptive styling based on LayoutTheme
 * - WCAG 2.1 AA compliant focus indicators
 */

import { useRolePersona } from '@/contexts/RolePersonaContext'
import { Link, useLocation } from 'react-router-dom'
import { useState, useRef, useEffect, KeyboardEvent } from 'react'
import { getVisibleCategories } from '@/types/navigation'
import type { NavCategory } from '@/types/navigation'

export function Navigation() {
  const { currentPersona } = useRolePersona()
  const location = useLocation()

  // State for dropdown visibility and mobile menu
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const navRef = useRef<HTMLElement>(null)

  // Get visible categories based on user role
  const visibleCategories = getVisibleCategories(
    currentPersona === 'candidate' ? null : 'admin' // Simplified role check
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setActiveDropdown(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false)
    setActiveDropdown(null)
  }, [location.pathname])

  const isActive = (path: string) => location.pathname === path
  const isCategoryActive = (category: NavCategory) =>
    category.links.some((link) => isActive(link.path))

  // UX1: Keyboard navigation handler
  const handleCategoryKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    categoryId: string
  ) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault()
        setActiveDropdown(activeDropdown === categoryId ? null : categoryId)
        break
      case 'Escape':
        setActiveDropdown(null)
        break
      case 'ArrowDown':
        event.preventDefault()
        setActiveDropdown(categoryId)
        break
      case 'ArrowUp':
        event.preventDefault()
        setActiveDropdown(null)
        break
    }
  }

  // UX1: Desktop categorized navigation with dropdowns
  return (
    <nav ref={navRef} className="nav-categorized">
      {/* Mobile hamburger button */}
      <button
        className="nav-categorized__mobile-toggle"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle navigation menu"
        aria-expanded={isMobileMenuOpen}
      >
        <span className="nav-categorized__hamburger-icon">
          {isMobileMenuOpen ? '✕' : '☰'}
        </span>
      </button>

      {/* Desktop: Horizontal category dropdowns */}
      <div className="nav-categorized__desktop">
        {visibleCategories.map((category) => (
          <div
            key={category.id}
            className={`nav-categorized__category ${
              isCategoryActive(category) ? 'nav-categorized__category--active' : ''
            } ${
              activeDropdown === category.id ? 'nav-categorized__category--open' : ''
            }`}
            onMouseEnter={() => setActiveDropdown(category.id)}
            onMouseLeave={() => setActiveDropdown(null)}
          >
            {/* Category trigger */}
            <div
              className="nav-categorized__category-trigger"
              role="button"
              tabIndex={0}
              aria-haspopup="true"
              aria-expanded={activeDropdown === category.id}
              onKeyDown={(e) => handleCategoryKeyDown(e, category.id)}
            >
              <span className="nav-categorized__category-icon" aria-hidden="true">
                {category.icon}
              </span>
              <span className="nav-categorized__category-label">
                {category.label}
              </span>
              <span className="nav-categorized__category-arrow" aria-hidden="true">
                {activeDropdown === category.id ? '▲' : '▼'}
              </span>
            </div>

            {/* Category dropdown */}
            {activeDropdown === category.id && (
              <div className="nav-categorized__dropdown">
                <div className="nav-categorized__dropdown-header">
                  <span className="nav-categorized__dropdown-icon">
                    {category.icon}
                  </span>
                  <div>
                    <div className="nav-categorized__dropdown-title">
                      {category.label}
                    </div>
                    <div className="nav-categorized__dropdown-description">
                      {category.description}
                    </div>
                  </div>
                </div>

                <div className="nav-categorized__dropdown-links">
                  {category.links.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`nav-categorized__dropdown-link ${
                        isActive(link.path)
                          ? 'nav-categorized__dropdown-link--active'
                          : ''
                      }`}
                    >
                      <div className="nav-categorized__dropdown-link-label">
                        {link.label}
                      </div>
                      {link.description && (
                        <div className="nav-categorized__dropdown-link-description">
                          {link.description}
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Mobile: Full-screen overlay with categories */}
      {isMobileMenuOpen && (
        <div className="nav-categorized__mobile-overlay">
          <div className="nav-categorized__mobile-content">
            {visibleCategories.map((category) => (
              <div key={category.id} className="nav-categorized__mobile-category">
                {/* Mobile category header */}
                <div className="nav-categorized__mobile-category-header">
                  <span
                    className="nav-categorized__mobile-category-icon"
                    aria-hidden="true"
                  >
                    {category.icon}
                  </span>
                  <div>
                    <div className="nav-categorized__mobile-category-label">
                      {category.label}
                    </div>
                    <div className="nav-categorized__mobile-category-description">
                      {category.description}
                    </div>
                  </div>
                </div>

                {/* Mobile category links */}
                <div className="nav-categorized__mobile-links">
                  {category.links.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`nav-categorized__mobile-link ${
                        isActive(link.path)
                          ? 'nav-categorized__mobile-link--active'
                          : ''
                      }`}
                    >
                      <div className="nav-categorized__mobile-link-label">
                        {link.label}
                      </div>
                      {link.description && (
                        <div className="nav-categorized__mobile-link-description">
                          {link.description}
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  )
}

Navigation.displayName = 'Navigation'
